import React, { useState, useRef } from 'react';
import { X, Upload, FileText, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { batchImportResumes } from '../api';

interface Props {
  positionId: string;
  positionName: string;
  positions?: { id: string; name: string }[];
  onClose: () => void;
  onImported: () => void;
}

interface ImportResult {
  status: 'success' | 'merged' | 'error';
  fileName: string;
  candidateName?: string;
  merged?: boolean;
  error?: string;
  parsed?: {
    phone?: string;
    education?: string;
    school?: string;
    work_years?: number;
    latest_company?: string;
    latest_title?: string;
  };
}

type Phase = 'select' | 'importing' | 'done';

export default function BatchImportModal({ positionId, positionName, positions = [], onClose, onImported }: Props) {
  const [files, setFiles] = useState<File[]>([]);
  const [phase, setPhase] = useState<Phase>('select');
  const [results, setResults] = useState<ImportResult[]>([]);
  const [summary, setSummary] = useState({ total: 0, successCount: 0, mergedCount: 0, errorCount: 0 });
  const [targetPositionId, setTargetPositionId] = useState<string>(positionId || '');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setFiles(prev => [...prev, ...newFiles]);
    }
    if (inputRef.current) inputRef.current.value = '';
  };

  const removeFile = (idx: number) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleImport = async () => {
    if (files.length === 0) return;
    if (!targetPositionId) { toast.error('请先选择导入到的岗位'); return; }
    setPhase('importing');
    try {
      const data = await batchImportResumes(files, targetPositionId);
      setResults(data.results);
      setSummary({ total: data.total, successCount: data.successCount, mergedCount: data.mergedCount || 0, errorCount: data.errorCount });
      setPhase('done');
      const okCount = (data.successCount || 0) + (data.mergedCount || 0);
      if (okCount > 0) {
        toast.success(data.message);
        onImported();
      } else {
        toast.error('全部导入失败，请检查文件格式');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || '批量导入失败');
      setPhase('select');
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const dropped = Array.from(e.dataTransfer.files).filter(f =>
      ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'].includes(f.type)
    );
    if (dropped.length > 0) setFiles(prev => [...prev, ...dropped]);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-200 shrink-0">
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-gray-800">批量导入简历</h3>
            {positions.length > 0 ? (
              <div className="flex items-center gap-1.5 mt-1">
                <span className="text-xs text-gray-400 shrink-0">本批全部导入到岗位：</span>
                <select
                  value={targetPositionId}
                  onChange={e => setTargetPositionId(e.target.value)}
                  disabled={phase !== 'select'}
                  className="text-xs font-medium text-gray-700 border border-gray-200 rounded px-1.5 py-0.5 max-w-[220px] focus:outline-none focus:ring-1 focus:ring-primary-400 disabled:bg-gray-50 disabled:text-gray-400"
                >
                  <option value="">请选择岗位</option>
                  {positions.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
            ) : (
              <p className="text-xs text-gray-400 mt-0.5">导入到岗位：<span className="font-medium text-gray-600">{positionName}</span></p>
            )}
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded"><X size={18} /></button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {phase === 'select' && (
            <div className="space-y-4">
              {/* Drop zone */}
              <div
                onDragOver={e => e.preventDefault()}
                onDrop={handleDrop}
                onClick={() => inputRef.current?.click()}
                className="border-2 border-dashed border-gray-200 hover:border-primary-300 rounded-xl p-8 text-center cursor-pointer transition-colors group"
              >
                <Upload size={32} className="mx-auto text-gray-300 group-hover:text-primary-400 transition-colors" />
                <p className="mt-3 text-sm text-gray-500">点击选择或拖拽简历文件到此处</p>
                <p className="text-xs text-gray-400 mt-1">支持 PDF、DOC、DOCX，最多 50 个文件</p>
                <input
                  ref={inputRef}
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </div>

              {/* File list */}
              {files.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-500">已选择 {files.length} 个文件</span>
                    <button onClick={() => setFiles([])} className="text-xs text-red-500 hover:text-red-600">清空</button>
                  </div>
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {files.map((f, i) => (
                      <div key={i} className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg">
                        <FileText size={14} className="text-gray-400 shrink-0" />
                        <span className="text-xs text-gray-600 flex-1 truncate">{f.name}</span>
                        <span className="text-[10px] text-gray-400 shrink-0">{(f.size / 1024).toFixed(0)}KB</span>
                        <button onClick={() => removeFile(i)} className="text-gray-400 hover:text-red-500 shrink-0">
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {phase === 'importing' && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 size={36} className="text-primary-500 animate-spin" />
              <p className="mt-4 text-sm font-medium text-gray-600">正在解析并导入 {files.length} 份简历...</p>
              <p className="text-xs text-gray-400 mt-1">请耐心等待，文件较多时需要一些时间</p>
            </div>
          )}

          {phase === 'done' && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                <div className="text-center flex-1">
                  <div className="text-xl font-bold text-gray-800">{summary.total}</div>
                  <div className="text-xs text-gray-400">总计</div>
                </div>
                <div className="text-center flex-1">
                  <div className="text-xl font-bold text-green-600">{summary.successCount}</div>
                  <div className="text-xs text-green-500">新增</div>
                </div>
                {summary.mergedCount > 0 && (
                  <div className="text-center flex-1">
                    <div className="text-xl font-bold text-blue-500">{summary.mergedCount}</div>
                    <div className="text-xs text-blue-400">已更新(去重)</div>
                  </div>
                )}
                {summary.errorCount > 0 && (
                  <div className="text-center flex-1">
                    <div className="text-xl font-bold text-red-500">{summary.errorCount}</div>
                    <div className="text-xs text-red-400">失败</div>
                  </div>
                )}
              </div>

              {/* Result list */}
              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {results.map((r, i) => {
                  const ok = r.status === 'success' || r.status === 'merged';
                  return (
                  <div key={i} className={clsx(
                    'flex items-start gap-2 px-3 py-2 rounded-lg text-xs',
                    r.status === 'success' ? 'bg-green-50' : r.status === 'merged' ? 'bg-blue-50' : 'bg-red-50'
                  )}>
                    {r.status === 'success' ? (
                      <CheckCircle2 size={14} className="text-green-500 shrink-0 mt-0.5" />
                    ) : r.status === 'merged' ? (
                      <CheckCircle2 size={14} className="text-blue-500 shrink-0 mt-0.5" />
                    ) : (
                      <AlertCircle size={14} className="text-red-500 shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700 truncate">
                          {ok ? r.candidateName : r.fileName}
                        </span>
                        {r.status === 'merged' && (
                          <span className="text-[10px] text-blue-500 bg-blue-100 px-1.5 py-0.5 rounded shrink-0">已更新</span>
                        )}
                        {ok && r.fileName !== r.candidateName && (
                          <span className="text-gray-400 truncate">({r.fileName})</span>
                        )}
                      </div>
                      {ok && r.parsed && (
                        <div className="text-[10px] text-gray-400 mt-0.5">
                          {[r.parsed.latest_company, r.parsed.latest_title, r.parsed.education, r.parsed.school].filter(Boolean).join(' · ')}
                        </div>
                      )}
                      {r.status === 'error' && (
                        <div className="text-[10px] text-red-400 mt-0.5">{r.error}</div>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-end gap-2 shrink-0">
          {phase === 'select' && (
            <>
              <button onClick={onClose} className="px-4 py-1.5 text-sm text-gray-500 hover:bg-gray-100 rounded-lg transition-colors">
                取消
              </button>
              <button
                onClick={handleImport}
                disabled={files.length === 0 || !targetPositionId}
                className="px-5 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-40"
              >
                开始导入 ({files.length})
              </button>
            </>
          )}
          {phase === 'done' && (
            <button onClick={onClose} className="px-5 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors">
              完成
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
